import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const Header = () => {
  const { cartCount, setIsCartOpen } = useCart();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'Our Story', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled || !isHomePage
          ? 'bg-[#FDF6E3]/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-4 group">
            <div className="relative">
              {/* Logo Option 2: HB Monogram with Wheat */}
              <svg 
                viewBox="0 0 60 60" 
                className="w-14 h-14 drop-shadow-lg group-hover:drop-shadow-xl transition-all duration-300"
              >
                <circle cx="30" cy="30" r="28" fill="url(#headerMonogramGradient)" />
                <circle cx="30" cy="30" r="25" fill="none" stroke="#FDF6E3" strokeWidth="0.5" opacity="0.4" />
                {/* H letter */}
                <path d="M18 18 L18 42 M18 30 L28 30 M28 18 L28 42" fill="none" stroke="#FDF6E3" strokeWidth="2.5" strokeLinecap="round" />
                {/* B letter */}
                <path d="M34 18 L34 42 M34 18 Q44 18 44 24 Q44 30 34 30 Q46 30 46 36 Q46 42 34 42" fill="none" stroke="#FDF6E3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                {/* Small wheat accent */}
                <path d="M50 14 Q48 18 50 22" fill="none" stroke="#D4A853" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="50" cy="12" r="2" fill="#D4A853" />
                <defs>
                  <linearGradient id="headerMonogramGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#C17817" />
                    <stop offset="100%" stopColor="#8B4513" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div className="flex flex-col">
              <span className={`font-serif text-2xl sm:text-3xl font-bold tracking-wide transition-colors duration-300 ${
                isScrolled || !isHomePage ? 'text-[#5C3D2E]' : 'text-[#FDF6E3]'
              }`}>
                House of Bania
              </span>
              <span className={`text-[10px] sm:text-xs tracking-[0.25em] uppercase transition-colors duration-300 ${
                isScrolled || !isHomePage ? 'text-[#8B6914]' : 'text-[#D4A853]'
              }`}>
                Heritage Taste
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative font-medium text-sm tracking-wide transition-colors duration-300 ${
                  isScrolled || !isHomePage
                    ? isActive(link.path)
                      ? 'text-[#C17817]'
                      : 'text-[#5C3D2E] hover:text-[#C17817]'
                    : isActive(link.path)
                    ? 'text-[#D4A853]'
                    : 'text-[#FDF6E3]/90 hover:text-[#D4A853]'
                } group`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-[#C17817] transition-all duration-300 ${
                    isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* Cart & Mobile Menu */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className={`relative transition-colors duration-300 ${
                isScrolled || !isHomePage
                  ? 'text-[#5C3D2E] hover:text-[#C17817] hover:bg-[#C17817]/10'
                  : 'text-[#FDF6E3] hover:text-[#D4A853] hover:bg-white/10'
              }`}
              onClick={() => setIsCartOpen(true)}
            >
              <ShoppingBag className="h-5 w-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C17817] text-[#FDF6E3] text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                  {cartCount}
                </span>
              )}
            </Button>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button
                  variant="ghost"
                  size="icon"
                  className={`transition-colors duration-300 ${
                    isScrolled || !isHomePage
                      ? 'text-[#5C3D2E] hover:bg-[#C17817]/10'
                      : 'text-[#FDF6E3] hover:bg-white/10'
                  }`}
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#FDF6E3] border-l-[#D4A853]/30 w-72">
                <div className="flex flex-col gap-6 mt-8">
                  <div className="flex items-center gap-3 pb-6 border-b border-[#D4A853]/30">
                    <svg viewBox="0 0 60 60" className="w-12 h-12">
                      <circle cx="30" cy="30" r="28" fill="url(#mobileMonogramGradient)" />
                      <circle cx="30" cy="30" r="25" fill="none" stroke="#FDF6E3" strokeWidth="0.5" opacity="0.4" />
                      <path d="M18 18 L18 42 M18 30 L28 30 M28 18 L28 42" fill="none" stroke="#FDF6E3" strokeWidth="2.5" strokeLinecap="round" />
                      <path d="M34 18 L34 42 M34 18 Q44 18 44 24 Q44 30 34 30 Q46 30 46 36 Q46 42 34 42" fill="none" stroke="#FDF6E3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M50 14 Q48 18 50 22" fill="none" stroke="#D4A853" strokeWidth="1.5" strokeLinecap="round" />
                      <circle cx="50" cy="12" r="2" fill="#D4A853" />
                      <defs>
                        <linearGradient id="mobileMonogramGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#C17817" />
                          <stop offset="100%" stopColor="#8B4513" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <span className="font-serif text-xl font-bold text-[#5C3D2E]">House of Bania</span>
                  </div>
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`text-lg font-medium transition-colors duration-300 ${
                        isActive(link.path)
                          ? 'text-[#C17817]'
                          : 'text-[#5C3D2E] hover:text-[#C17817]'
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
